// global
import download from 'downloadjs';
// local
import { getToken } from 'app/storage/auth';
import ApiError from 'app/utils/apiUtils/apiError';
import backendEnv from 'environments/backend.env';

/**
 * Append the backend URL to the path.
 * @param {*} path Path to be appended.
 */
const urlFromPath = path => `${backendEnv}${path}`;

/**
 * Default headers that are sent from the backend.
 */
const DEFAULT_HEADERS = {
  Accept: 'application/json',
  'Content-Type': 'application/json'
};

/**
 * Default handler for DOWNLOADs types of request.
 *  @param {HTTP Response} response response from the backend.
 */
const downloadHandler = response => {
  const fileName = response.headers.get('content-file-name');
  const fileType = response.headers.get('content-type');
  return response.blob().then(blob => download(blob, fileName, fileType));
};

/**
 * Default handler for DOWNLOADs types of request.
 *  @param {HTTP Response} response response from the backend.
 */
const downloadReport = response => {
  const fileName = response.headers.get('content-file-name');
  const fileType = response.headers.get('content-type');
  return response.blob().then(blob => {
    download(blob, fileName, fileType);

    const newTab = window.open('', '_blank');

    const reader = new FileReader();
    reader.onload = function () {
      const uri = this.result;
      newTab.document.write(
        `<html><head><title>${fileName}</title></head><body style="margin: 0;"><embed width="100%" height="100%" src="${uri}" type="application/pdf"></embed></body></html>`
      );
    };
    reader.readAsDataURL(blob);
  });
};

/**
 * Default handler for JSON types of request.
 * @param {HTTP Response} response response from the backend.
 */
const jsonHandler = response => {
  return response.json().then(({ message }) => message);
};

/**
 * Default handler for ERRORs.
 * @param {HTTP Response} response response from the backend.
 */
const errorHandler = response =>
  response.json().then(({ statusCode, errors, title }) => {
    throw new ApiError(statusCode, errors, title);
  });

/**
 * Default handler that checks if the response is an error or not.
 * @param {HTTP Response} response response from the backend.
 */
const defaultHandler = handler => response =>
  response.status === 200 ? handler(response) : errorHandler(response);

/**
 * API method to call the backend.
 * @param {String} method HTTP method of the request.
 * @param {Method} handle Method to handle.
 * @param {String} url String of the HTTP Request.
 * @param {Any} data Data to be sent to the backend.
 * @param {Boolean} appendToken true if the token should be appended to the HTTP Request header.
 */
const API = (method, handle, url, data = null, appendToken = true) => {
  const options = {
    method: method.toUpperCase(),
    headers: DEFAULT_HEADERS
  };
  if (data) {
    options.body = JSON.stringify(data);
  }
  if (appendToken) {
    options.headers['Authorization'] = `Bearer ${getToken()}`;
  }
  return fetch(urlFromPath(url), options).then(response =>
    defaultHandler(handle)(response.clone())
  );
};

/**
 * API method for upload.
 * @param {String} method HTTP method of the request.
 * @param {Method} handle Method to handle.
 * @param {String} url String of the HTTP Request.
 * @param {FormData} data Data to be sent to the backend.
 * @param {Boolean} appendToken true if the token should be appended to the HTTP Request header.
 */
const API_UPLOAD = (method, handle, url, data = null, appendToken = true) => {
  const options = {
    method: method.toUpperCase(),
    headers: {
      Connection: 'Keep-Alive',
      'Keep-Alive': 'timeout=300, max=1000'
    },
    keepalive: true
  };
  if (data) {
    options.body = data;
  }
  if (appendToken) {
    options.headers['Authorization'] = `Bearer ${getToken()}`;
  }
  return fetch(urlFromPath(url), options).then(response =>
    defaultHandler(handle)(response.clone())
  );
};

// Bind all the convinient methods (GET, POST, PUT, DELETE) to the API.
['get', 'post', 'put', 'delete'].forEach(method => {
  API[method] = API.bind(null, method, jsonHandler);
});

API['download'] = API.bind(null, 'POST', downloadHandler);
API['downloadReport'] = API.bind(null, 'POST', downloadReport);

API['upload'] = API_UPLOAD.bind(null, 'POST', jsonHandler);

export const mountQueryString = params => {
  if (params) {
    let queryString = '';
    Object.keys(params).forEach(p => {
      if (params[p]) {
        queryString += `${!queryString.length ? '?' : '&'}${p}=${params[p]}`;
      }
    });
    return queryString;
  } else {
    return '';
  }
};

export default API;
